import { Routes } from '@angular/router';
import { canActivateAuthGuard } from '../auth/guards/can-activate-auth.guard';
import { canVisitGuard } from '../auth/guards/can-visit.guard';
import { hasPermissionGuard } from '../auth/guards/has-permission-guard';
import { isLoggedInGuard } from '../auth/guards/is-logged-in.guard';
import { hasUnsavedChangesGuard } from '../navigation/has-unsaved-changes.guard';
import { PermissionEnum } from '../auth/models/permissions-enum.model';

export const routes: Routes = [
    // ============================================
    // PRESENTATION LAYOUT (Public/Guest pages)
    // ============================================
    { 
        path: '', 
        loadComponent: () => import('./layouts/presentation-layout/presentation-layout.component')
            .then(m => m.PresentationLayoutComponent),
        canActivate: [canVisitGuard],
        children: [
            { 
                path: '', 
                loadComponent: () => import('../presentation/home/home.component')
                    .then(m => m.HomeComponent) 
            },
            { 
                path: 'home', 
                loadComponent: () => import('../presentation/home/home.component')
                    .then(m => m.HomeComponent) 
            },
            { 
                path: 'login', 
                loadComponent: () => import('../auth/login/login.component')
                    .then(m => m.LoginComponent) 
            },
            { 
                path: 'register', 
                loadComponent: () => import('../auth/register/register.component')
                    .then(m => m.RegisterComponent) 
            },
            { 
                path: 'forgot-password', 
                loadComponent: () => import('../user/forgot-password/forgot-password.component')
                    .then(m => m.ForgotPasswordComponent) 
            },
            { 
                path: 'reset-password', 
                loadComponent: () => import('../user/reset-password/reset-password.component')
                    .then(m => m.ResetPasswordComponent) 
            },
            { 
                path: 'confirm-account', 
                loadComponent: () => import('../auth/confirm-email/confirm-email.component')
                    .then(m => m.ConfirmEmailComponent) 
            },
            { 
                path: 'registration-success', 
                loadComponent: () => import('../auth/registration-success/registration-success.component')
                    .then(m => m.RegistrationSuccessComponent ) 
            },
                        {
                path: 'subscriptions',
                loadComponent: () => import('../subscription-plans-unauthorized/subscription-plans-unauthorized.component')
                    .then(m => m.SubscriptionPlansUnauthorizedComponent)
            },
        ]
    },

    // ============================================
    // MAIN LAYOUT (Authenticated pages)
    // ============================================
    {
        path: '',
        loadComponent: () => import('./layouts/main-layout/main-layout.component')
            .then(m => m.MainLayoutComponent),
        canActivate: [canActivateAuthGuard],
        children: [
            { 
                path: 'profile', 
                loadComponent: () => import('../user/user-profile/user-profile.component')
                    .then(m => m.UserProfileComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.ViewProfile, PermissionEnum.UpdateUsers], requireAll: true }
            },
            {
                path: 'settings',
                loadComponent: () => import('../user/user-settings/user-settings.component')
                    .then(m => m.UserSettingsComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.ViewProfile] }
            },
            
            // ------------------------------------------
            // Feedback
            // ------------------------------------------
            {
                path: 'feedback',
                loadComponent: () => import('../feedback/feedback-list/feedback-list.component')
                    .then(m => m.FeedbackListComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.ViewFeedback] }
            },
            {
                path: 'feedback-management',
                loadComponent: () => import('../feedback/feedback-list/feedback-list.component')
                    .then(m => m.FeedbackListComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.AddAdminFeedBackMessage, PermissionEnum.ViewAllFeedBack], requireAll: true }
            },
            { 
                path: 'simulation-management', 
                loadComponent: () => import('../simulation/simulation-management/simulation-management.component')
                    .then(m => m.SimulationManagementComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: PermissionEnum.ViewSimulation },
                canDeactivate: [hasUnsavedChangesGuard]
            },
            { 
                path: 'configuration-management', 
                loadComponent: () => import('../configurations/configuration-management/configuration-management.component')
                    .then(m => m.ConfigurationManagementComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.AddConfigurations, PermissionEnum.UpdateConfigurations, PermissionEnum.DeleteConfigurations] },
                canDeactivate: [hasUnsavedChangesGuard]
            },
            { 
                path: 'user-management', 
                loadComponent: () => import('../user/user-administration/user-administration.component')
                    .then(m => m.UserAdministrationComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.DeleteUsers] }
            },
            { 
                path: 'payment-management', 
                loadComponent: () => import('../payment/payment-management/payment-management.component')
                    .then(m => m.PaymentManagementComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.ViewConfigurations, PermissionEnum.PaymentManagement] }
            },
            {
                path: 'question-management',
                loadComponent: () => import('../questions/question-management/question-management.component')
                    .then(m => m.QuestionManagementComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.AddQuestion] }
            },

            // ------------------------------------------
            // User: Simulations & Sessions
            // ------------------------------------------
            { 
                path: 'user-simulations', 
                loadComponent: () => import('../simulation/user-simulation-list/user-simulation-list.component')
                    .then(m => m.UserSimulationListComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: PermissionEnum.ViewOwnSimulation },
            },
            {
                path: 'simulation-ranking/:simulationId', 
                loadComponent: () => import('../simulation/simulation-ranking/simulation-ranking.component')
                    .then(m => m.SimulationRankingComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.ViewOwnSimulation, PermissionEnum.ViewRanking] },
            },
            { 
                path: 'user-simulation-session/:id', 
                loadComponent: () => import('../simulation/user-simulation-session/user-simulation-session.component')
                    .then(m => m.UserSimulationSessionComponent),
                canDeactivate: [hasUnsavedChangesGuard],
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: PermissionEnum.ViewOwnSimulation }
            },  
            { 
                path: 'retry-user-simulation/:id', 
                loadComponent: () => import('../simulation/simulation-retry-session/simulation-retry-session.component')
                    .then(m => m.SimulationRetrySessionComponent),
                canDeactivate: [hasUnsavedChangesGuard],
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: PermissionEnum.ViewOwnSimulation }
            },
            {
                path: 'dashboard', 
                loadComponent: () => import('../dashboard/dashboard.component')
                    .then(m => m.DashboardComponent),
                canActivate: [hasPermissionGuard],
                data: {requiredPermissions: [PermissionEnum.ViewOwnSession]}
            },           
            {
                path: 'session-configuration',
                loadComponent: () =>
                    import('../dashboard/session-configuration/session-configuration.component')
                    .then(m => m.SessonConfigurationComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.AddOneSession, PermissionEnum.AddMultipleSessions] }
            },
            { 
                path: 'user-session/:id', 
                loadComponent: () => 
                    import('../session/user-study-session/user-study-session.component')
                .then(m => m.UserStudySessionComponent),
                canActivate: [hasPermissionGuard],
                data: { requiredPermissions: [PermissionEnum.AddOneSession, PermissionEnum.AddMultipleSessions] }
             },
            // ------------------------------------------
            // Payment
            // ------------------------------------------
            {
                path: 'simulation-payment/:simulationId',
                loadComponent: () => import('../payment/simulation-payment/simulation-payment.component')
                    .then(m => m.SimulationPaymentComponent),
                canActivate: [isLoggedInGuard]
            },
            {
                path: 'subscription-plans',
                loadComponent: () => import('../subscription-plans/subscription-plans.component')
                    .then(m => m.SubscriptionPlansComponent),
                canActivate: [isLoggedInGuard]
            },
            {
                path: 'subscription-payment/:planId',
                loadComponent: () => import('../payment/subscription-payment/subscription-payment.component')
                    .then(m => m.SubscriptionPaymentComponent),
                canActivate: [isLoggedInGuard]
            },
            // ------------------------------------------
            // Auth
            // ------------------------------------------
            {
                path: 'logout',
                loadComponent: () => import('../auth/logout/logout.component')
                    .then(m => m.LogoutComponent),
                canActivate: [isLoggedInGuard],
            }           
        ],
    },

    // ============================================
    // STANDALONE PAGES (No layout wrapper)
    // ============================================
    { 
        path: 'support', 
        loadComponent: () => import('../shared/pages/support/support.component')
            .then(m => m.SupportComponent) 
    },
    {
        path: 'privacy-policy',
        loadComponent: () => import('../gdpr/privacy-policy/privacy-policy.component')
            .then(m => m.PrivacyPolicyComponent),
        title: 'Politica de Confidențialitate'
    },
    {
        path: 'cookie-policy',
        loadComponent: () => import('../gdpr/cookie-policy/cookie-policy.component')
            .then(m => m.CookiePolicyComponent),
        title: 'Politica de Cookie-uri'
    },
    {
        path: 'terms',
        loadComponent: () => import('../gdpr/terms-conditions/terms-conditions.component')
            .then(m => m.TermsConditionsComponent),
        title: 'Termenii și Condițiile'
    },

    // ============================================
    // ERROR PAGES
    // ============================================
    { 
        path: 'unauthorized', 
        loadComponent: () => import('../exception-handling/unauthorized/unauthorized.component')
            .then(m => m.UnauthorizedComponent) 
    },
    { 
        path: '**', 
        loadComponent: () => import('../exception-handling/not-found/not-found.component')
            .then(m => m.NotFoundComponent) 
    }
];